/**
 * Kalaboost — ajoute plusieurs unités via le formulaire produit de la fiche.
 */

import { StandardEvents } from '@shopify/events';

const ROOT_SELECTOR = '[data-kalaboost-guarantee-add]';
const BUTTON_SELECTOR = '[data-kalaboost-guarantee-add-button]';

/**
 * @param {unknown} value
 * @returns {number}
 */
function toId(value) {
  return Number(value) || 0;
}

/**
 * @param {Element} root
 * @returns {{ productId: number, variantId: number, quantity: number, error: string } | null}
 */
function readConfig(root) {
  const node = root.querySelector('[data-kalaboost-guarantee-add-config]');
  if (!node?.textContent) return null;
  try {
    const parsed = JSON.parse(node.textContent);
    return {
      productId: toId(parsed.productId),
      variantId: toId(parsed.variantId),
      quantity: Math.max(2, Number(parsed.quantity) || 2),
      error: parsed.error || "Impossible d'ajouter les produits. Réessayez.",
    };
  } catch (error) {
    console.warn('[kalaboost-guarantee-add] Config JSON invalide', error);
    return null;
  }
}

class KalaboostGuaranteeAdd {
  /** @type {Element} */
  #root;

  /** @type {{ productId: number, variantId: number, quantity: number, error: string } | null} */
  #config;

  /** @type {boolean} */
  #busy = false;

  /**
   * @param {Element} root
   */
  constructor(root) {
    this.#root = root;
    this.#config = readConfig(root);
    if (!this.#config?.variantId) return;
    this.#root.addEventListener('click', this.#onClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest(BUTTON_SELECTOR)) return;
    event.preventDefault();
    this.#add();
  };

  /**
   * @returns {HTMLElement & { addToCart?: (variantId: string, quantity: number) => void } | null}
   */
  #productForm() {
    const section = this.#root.closest('.shopify-section');
    const form = section?.querySelector('product-form-component');
    return form instanceof HTMLElement ? form : null;
  }

  #variantId() {
    const form = this.#productForm();
    const pageProductId = toId(form?.dataset.productId);
    if (pageProductId && this.#config?.productId && pageProductId === this.#config.productId) {
      const input = form.querySelector('input[name="id"]');
      if (input instanceof HTMLInputElement && input.value) return input.value;
    }
    return String(this.#config?.variantId || '');
  }

  #add() {
    if (this.#busy || !this.#config) return;

    const form = this.#productForm();
    if (!form || typeof form.addToCart !== 'function') {
      this.#setError(this.#config.error);
      return;
    }

    this.#setBusy(true);
    this.#setError('');

    const onUpdate = (event) => {
      const finish = () => this.#setBusy(false);
      if (event?.promise && typeof event.promise.then === 'function') {
        event.promise.then(finish).catch(finish);
        return;
      }
      finish();
    };

    document.addEventListener(StandardEvents.cartLinesUpdate, onUpdate, { once: true });
    document.addEventListener(StandardEvents.cartError, onUpdate, { once: true });

    form.addToCart(this.#variantId(), this.#config.quantity);
  }

  /**
   * @param {string} message
   */
  #setError(message) {
    const node = this.#root.querySelector('[data-kalaboost-guarantee-add-error]');
    if (!(node instanceof HTMLElement)) return;
    node.textContent = message;
    node.hidden = message === '';
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    const button = this.#root.querySelector(BUTTON_SELECTOR);
    if (!(button instanceof HTMLElement)) return;
    if (busy) {
      button.setAttribute('disabled', '');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
    }
  }

  destroy() {
    this.#root.removeEventListener('click', this.#onClick);
  }
}

const instances = new WeakMap();

function init(scope = document) {
  scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    if (instances.has(root)) return;
    instances.set(root, new KalaboostGuaranteeAdd(root));
  });
}

document.addEventListener('shopify:section:unload', (event) => {
  event.target?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
    instances.get(root)?.destroy();
    instances.delete(root);
  });
});

document.addEventListener('shopify:section:load', (event) => init(event.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}
